import { useQuery } from '@tanstack/react-query';
import { ArrowRight, LogOut, Shield } from 'lucide-react';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { EmptyState, Skeleton } from '@/components/ui/misc';
import { useAuth } from '@/lib/auth';
import { AuthLayout } from './login-page';

/** After sign-in: choose which stable to manage (skipped automatically when there is only one). */
export function StablePicker() {
  const { stables, selectTenant, logout, me, isPlatformAdmin } = useAuth();
  const navigate = useNavigate();
  const q = useQuery({ queryKey: ['my-stables'], queryFn: stables });
  const list = q.data ?? [];

  useEffect(() => {
    if (list.length === 1) selectTenant(list[0].slug);
  }, [list.length]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <AuthLayout title="Choose a stable" subtitle="You have access to the stables below.">
      <div className="space-y-2">
        {q.isLoading && Array.from({ length: 2 }).map((_, i) => <Skeleton key={i} className="h-16 rounded-xl" />)}
        {list.map((s) => (
          <button key={s.id} onClick={() => selectTenant(s.slug)} className="group flex w-full items-center gap-4 rounded-xl border bg-surface p-4 text-start shadow-soft transition-all hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-lift">
            <div className="grid size-11 place-items-center rounded-xl bg-primary/10 font-display text-lg font-semibold text-primary">{s.name.charAt(0)}</div>
            <div className="min-w-0 flex-1">
              <div className="truncate font-medium">{s.name}</div>
              <div className="text-xs text-muted-foreground">{s.slug}</div>
            </div>
            <ArrowRight className="size-4 text-muted-foreground transition-transform group-hover:translate-x-0.5 rtl:rotate-180" />
          </button>
        ))}
        {!q.isLoading && list.length === 0 && (
          <EmptyState
            title={me?.user.isPlatformAdmin || isPlatformAdmin ? 'Platform administrator' : 'No stables yet'}
            description={me?.user.isPlatformAdmin || isPlatformAdmin ? 'You are not a member of any stable. Open one — or create a new one — from the platform console.' : 'Ask a stable owner to invite you to their stable.'}
          />
        )}
      </div>
      <div className="mt-6 flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={() => void logout()}>
          <LogOut /> Sign out
        </Button>
        {isPlatformAdmin && (
          <Button variant="outline" size="sm" onClick={() => navigate('/platform')}>
            <Shield /> Platform console
          </Button>
        )}
      </div>
    </AuthLayout>
  );
}
